import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { IoMdClose } from "react-icons/io";
import Helper from "../../helper";
import "./PlaceBet.css"; // Import the CSS

const formatStakeChipLabel = (amount) => {
  const value = Number(amount);

  if (!Number.isFinite(value)) {
    return "";
  }

  if (value >= 10000000) {
    const croreValue = value / 10000000;

    return `+${
      Number.isInteger(croreValue) ? croreValue : croreValue.toFixed(1)
    }Cr`;
  }

  if (value >= 100000) {
    const lakhValue = value / 100000;

    return `+${
      Number.isInteger(lakhValue) ? lakhValue : lakhValue.toFixed(1)
    }L`;
  }

  if (value >= 1000) {
    const thousandValue = value / 1000;

    return `+${
      Number.isInteger(thousandValue) ? thousandValue : thousandValue.toFixed(1)
    }k`;
  }

  return `+${value}`;
};

const DEFAULT_STAKE_CHIPS = [
  { id: "stake-100", label: "+100", value: 100 },
  { id: "stake-200", label: "+200", value: 200 },
  { id: "stake-500", label: "+500", value: 500 },
  { id: "stake-1k", label: "+1000", value: 1000 },
  { id: "stake-2k", label: "+2000", value: 2000 },
  { id: "stake-5k", label: "+5000", value: 5000 },
  { id: "stake-10k", label: "+10000", value: 10000 },
  { id: "stake-25k", label: "+25000", value: 25000 },
];

const PlaceBet = (props) => {
  const userInfo = Helper();
  const rawBetSide =
    props?.BetPlaceData?.is_back ?? props?.BetPlaceData?.isBack ?? props?.type;

  const normalizedBetSide = String(rawBetSide).trim().toLowerCase();

  const isBack =
    rawBetSide === true ||
    rawBetSide === 1 ||
    normalizedBetSide === "1" ||
    normalizedBetSide === "back" ||
    normalizedBetSide === "yes";

  const isLay =
    rawBetSide === false ||
    rawBetSide === 0 ||
    normalizedBetSide === "0" ||
    normalizedBetSide === "lay" ||
    normalizedBetSide === "no" ||
    normalizedBetSide === "false";

  const rawFancyValue =
    props?.BetPlaceData?.is_fancy ??
    props?.BetPlaceData?.isFancy ??
    props?.BetPlaceData?.market_type;

  const normalizedFancyValue = String(rawFancyValue).trim().toLowerCase();

  const isFancyMarket =
    rawFancyValue === true ||
    rawFancyValue === 1 ||
    normalizedFancyValue === "1" ||
    normalizedFancyValue === "yes" ||
    normalizedFancyValue === "true" ||
    normalizedFancyValue === "fancy" ||
    normalizedFancyValue === "session";

  /*
   * Same side mapping for every market:
   * Back / Fancy Yes = blue
   * Lay / Fancy No   = pink
   */
  const displayIsBack = isBack;
  const displayIsLay = isLay;
  const isDesktopInline = props?.variant === "desktop-inline";
  // console.log(
  //   "PlaceBet props:",
  //   props?.BetPlaceData?.team1,
  //   props?.BetPlaceData?.team2,
  //   props?.BetPlaceData?.team3,
  // );
  // console.log("PlaceBet props:", props?.BetPlaceData?.runner_name);

  const [editableOdds, setEditableOdds] = useState(() =>
    Number(props?.BetPlaceData?.price || 0),
  );

  const rawAdminChips = Array.isArray(props?.chips)
    ? props.chips
    : Array.isArray(props?.chips?.resultData)
      ? props.chips.resultData
      : Array.isArray(props?.chips?.data)
        ? props.chips.data
        : [];

  const normalizedAdminChips = rawAdminChips
    .map((chip, index) => {
      /*
       * API primitive number bhej sakti hai:
       * 1000
       *
       * Ya object:
       * {
       *   value: 1000,
       *   label: "1k"
       * }
       *
       * Neeche common field names support kiye gaye hain.
       */
      const rawValue =
        typeof chip === "number" || typeof chip === "string"
          ? chip
          : (chip?.value ??
            chip?.amount ??
            chip?.stake ??
            chip?.chip_value ??
            chip?.chips_value ??
            chip?.button_value ??
            chip?.stake_value);

      const numericValue = Number(rawValue);

      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return null;
      }

      const adminLabel =
        typeof chip === "object" && chip !== null
          ? (chip?.label ??
            chip?.name ??
            chip?.chip_name ??
            chip?.chips_name ??
            chip?.button_name)
          : null;

      return {
        id: chip?._id ?? chip?.id ?? `${numericValue}-${index}`,

        value: numericValue,

        label: adminLabel
          ? String(adminLabel).startsWith("+")
            ? String(adminLabel)
            : `+${adminLabel}`
          : formatStakeChipLabel(numericValue),
      };
    })
    .filter(Boolean);

  const initialStakeChips =
    normalizedAdminChips.length > 0
      ? normalizedAdminChips
      : DEFAULT_STAKE_CHIPS;

  const stakeStorageKey = `placebet-stake-buttons-${userInfo?._id || "guest"}`;

  const [desktopStakeChips, setDesktopStakeChips] = useState(() => {
    try {
      const savedChips = localStorage.getItem(stakeStorageKey);

      if (savedChips) {
        const parsedChips = JSON.parse(savedChips);

        if (Array.isArray(parsedChips) && parsedChips.length > 0) {
          return parsedChips;
        }
      }
    } catch (error) {
      console.error("Unable to read saved stake buttons:", error);
    }

    return initialStakeChips;
  });

  const [activeDesktopChipId, setActiveDesktopChipId] = useState(null);

  const [showStakeEditor, setShowStakeEditor] = useState(false);

  const [stakeButtonDrafts, setStakeButtonDrafts] = useState([]);

  const openStakeButtonEditor = () => {
    setStakeButtonDrafts(
      desktopStakeChips.map((chip, index) => ({
        id: chip?.id || `stake-${index}`,
        label: String(chip?.label || "").replace(/^\+/, ""),
        value: String(chip?.value ?? ""),
      })),
    );

    setShowStakeEditor(true);
  };

  const closeStakeButtonEditor = () => {
    setShowStakeEditor(false);
    setStakeButtonDrafts([]);
    toast.success("Update Button Value");
  };

  const handleStakeDraftChange = (index, field, newValue) => {
    setStakeButtonDrafts((previousDrafts) =>
      previousDrafts.map((draft, draftIndex) =>
        draftIndex === index
          ? {
              ...draft,
              [field]: newValue,
            }
          : draft,
      ),
    );
  };

  const updateStakeButtons = () => {
    const hasInvalidButton = stakeButtonDrafts.some((button) => {
      const label = String(button?.label || "").trim();

      const value = Number(button?.value);

      return !label || !Number.isFinite(value) || value <= 0;
    });

    if (hasInvalidButton) {
      toast.error("Please enter a valid label and value for every button.");

      return;
    }

    const updatedButtons = stakeButtonDrafts.map((button, index) => {
      const cleanLabel = String(button?.label || "")
        .trim()
        .replace(/^\+/, "");

      return {
        id: button?.id || `stake-${index}`,
        label: `+${cleanLabel}`,
        value: Number(button.value),
      };
    });

    setDesktopStakeChips(updatedButtons);

    try {
      localStorage.setItem(stakeStorageKey, JSON.stringify(updatedButtons));
    } catch (error) {
      console.error("Unable to save stake buttons:", error);

      toast.error("Button values were updated, but could not be saved.");

      return;
    }

    setShowStakeEditor(false);
    setStakeButtonDrafts([]);

    // Show success message after the update.
    toast.success("Update Button Value");
  };

  useEffect(() => {
    const receivedOdds = Number(props?.BetPlaceData?.price || 0);

    setEditableOdds(receivedOdds);
  }, [
    props?.BetPlaceData?.price,
    props?.BetPlaceData?.selection_id,
    props?.BetPlaceData?.market_id,
  ]);

  const updateEditableOdds = (nextValue) => {
    let numericValue = Number(nextValue);

    if (!Number.isFinite(numericValue)) {
      numericValue = 1.01;
    }

    numericValue = Math.max(1.01, numericValue);
    numericValue = Number(numericValue.toFixed(2));

    setEditableOdds(numericValue);

    props?.setBetPlaceData?.((previousData) => ({
      ...previousData,
      price: numericValue,
    }));
  };

  const increaseDesktopOdds = () => {
    updateEditableOdds(Number(editableOdds || 0) + 0.02);
  };

  const decreaseDesktopOdds = () => {
    updateEditableOdds(Number(editableOdds || 0) - 0.02);
  };

  const desktopRunnerName =
    props?.BetPlaceData?.runner_name ||
    props?.BetPlaceData?.selection_name ||
    props?.BetPlaceData?.team_name ||
    "-";

  const desktopEventName =
    props?.BetPlaceData?.event_name ||
    props?.BetPlaceData?.eventName ||
    props?.BetPlaceData?.match_name ||
    "";

  const desktopSelectionLabel = `${desktopRunnerName} - ${
    displayIsLay ? "NO" : "YES"
  }`;

  const mobileMarketName =
    props?.BetPlaceData?.market_name || desktopRunnerName;

  const mobileEventName =
    props?.BetPlaceData?.event_name || props?.BetPlaceData?.eventName || "";

  const mobileMinBet =
    props?.BetPlaceData?.min_bet ?? props?.BetPlaceData?.minBet ?? "-";

  const mobileMaxBet =
    props?.BetPlaceData?.max_bet ?? props?.BetPlaceData?.maxBet ?? "-";

  const desktopOdds = Number(editableOdds || 0);

  const desktopStake = Number(props?.StakeValue ?? 0);

  const desktopProfit =
    desktopStake > 0
      ? isBack
        ? (desktopOdds - 1) * desktopStake
        : desktopStake
      : 0;

  const clearStakeAmount = () => {
    props?.placeStakeValue?.(0);
  };

  const addDesktopStake = (amount) => {
    const currentStake = Number(props?.StakeValue ?? 0);
    props?.placeStakeValue?.(currentStake + Number(amount));
  };

  const setMinimumStake = () => {
    const values = desktopStakeChips
      .map((chip) => Number(chip?.value))
      .filter((value) => Number.isFinite(value) && value > 0);

    props?.placeStakeValue?.(values.length > 0 ? Math.min(...values) : 0);
  };

  const setMaximumStake = () => {
    const values = desktopStakeChips
      .map((chip) => Number(chip?.value))
      .filter((value) => Number.isFinite(value) && value > 0);

    props?.placeStakeValue?.(values.length > 0 ? Math.max(...values) : 0);
  };

  const closePlaceBetManually = () => {
    props?.setShowBetsSlip?.(false);
    props?.setSelection_id?.("");
    props?.setHtmlId?.("");
    props?.placeStakeValue?.(0);
    props?.handleClosePlaceBet?.();
  };

  const stakeButtonEditorModal = showStakeEditor ? (
    <div
      className="stake-editor-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeStakeButtonEditor();
        }
      }}
    >
      <section
        className="stake-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stake-editor-title"
      >
        <header className="stake-editor-header">
          <h3 id="stake-editor-title">Set Button Value</h3>

          <button
            type="button"
            className="stake-editor-close"
            onClick={closeStakeButtonEditor}
            aria-label="Close editor"
          >
            <IoMdClose aria-hidden="true" />
          </button>
        </header>

        <div className="stake-editor-tabs">
          <button
            type="button"
            className="stake-editor-tab stake-editor-tab-active"
          >
            Game Buttons
          </button>

          <button type="button" className="stake-editor-tab" disabled>
            Casino Buttons
          </button>
        </div>

        <div className="stake-editor-column-headings">
          <span>Price Label:</span>
          <span>Price Value:</span>
        </div>

        <div className="stake-editor-fields">
          {stakeButtonDrafts.map((button, index) => (
            <div className="stake-editor-field-row" key={button?.id || index}>
              <input
                type="text"
                value={button?.label || ""}
                onChange={(event) =>
                  handleStakeDraftChange(index, "label", event.target.value)
                }
                placeholder="Label"
                aria-label={`Stake button ${index + 1} label`}
              />

              <input
                type="number"
                min="1"
                step="1"
                value={button?.value || ""}
                onChange={(event) =>
                  handleStakeDraftChange(index, "value", event.target.value)
                }
                placeholder="Value"
                aria-label={`Stake button ${index + 1} value`}
              />
            </div>
          ))}
        </div>

        <footer className="stake-editor-footer">
          <button
            type="button"
            className="stake-editor-update"
            onClick={updateStakeButtons}
          >
            Update
          </button>
        </footer>
      </section>
    </div>
  ) : null;

  if (isDesktopInline) {
    return (
      <>
        <section
          className={[
            "desktop-placebet",
            displayIsLay ? "desktop-placebet-lay" : "desktop-placebet-back",
            isFancyMarket
              ? "desktop-placebet-fancy"
              : "desktop-placebet-standard",
          ].join(" ")}
        >
          {/* Green title */}
          <header className="desktop-placebet-slip-header">
            <strong>BET SLIP</strong>

            <button
              type="button"
              className="desktop-placebet-close"
              onClick={closePlaceBetManually}
              aria-label="Close bet slip"
            >
              ×
            </button>
          </header>

          <div className="desktop-placebet-body">
            <div className="desktop-placebet-context">
              {desktopEventName && (
                <span className="desktop-placebet-event-name">
                  {desktopEventName}
                </span>
              )}

              <strong className="desktop-placebet-selection-name">
                {desktopSelectionLabel}
              </strong>
            </div>

            {/* Selected bet */}
            <div className="desktop-placebet-fields">
              <div className="desktop-odds-spinner">
                <span className="desktop-placebet-odds-label">Odds</span>
                <button
                  type="button"
                  className="desktop-odds-control desktop-odds-increase"
                  disabled
                  aria-label="Increase odds"
                >
                  +
                </button>

                <input
                  type="text"
                  className="desktop-placebet-odds"
                  value={editableOdds}
                  inputMode="decimal"
                  readOnly
                  aria-label="Bet odds"
                />

                <button
                  type="button"
                  className="desktop-odds-control desktop-odds-decrease"
                  disabled
                  aria-label="Decrease odds"
                >
                  −
                </button>
              </div>

              <input
                type="number"
                min="0"
                step="1"
                className="desktop-placebet-stake"
                value={props?.StakeValue ?? 0}
                onChange={(event) => {
                  const rawValue = event.target.value;

                  if (rawValue === "") {
                    props?.placeStakeValue?.(0);
                    return;
                  }

                  const value = Number(rawValue);

                  if (Number.isFinite(value) && value >= 0) {
                    props?.placeStakeValue?.(value);
                  }
                }}
                onWheel={(event) => {
                  event.currentTarget.blur();
                }}
                aria-label="Stake amount"
              />
            </div>

            {/* Dynamic admin chips */}
            <div className="desktop-placebet-chips">
              {desktopStakeChips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className={[
                    "desktop-placebet-chip",
                    activeDesktopChipId === chip.id ? "is-active" : "",
                  ].join(" ")}
                  onClick={() => {
                    setActiveDesktopChipId(chip.id);
                    addDesktopStake(chip.value);
                  }}
                >
                  {chip.value}
                </button>
              ))}
            </div>

            {/* Quick actions */}
            <div className="desktop-placebet-quick-actions">
              <button
                type="button"
                className="desktop-placebet-min"
                onClick={setMinimumStake}
              >
                MIN
              </button>

              <button
                type="button"
                className="desktop-placebet-max"
                onClick={setMaximumStake}
              >
                MAX
              </button>

              <button
                type="button"
                className="desktop-placebet-edit"
                onClick={openStakeButtonEditor}
              >
                EDIT STAKE
              </button>

              <button
                type="button"
                className="desktop-placebet-clear"
                onClick={clearStakeAmount}
              >
                CLEAR
              </button>
            </div>

            {/* Final actions */}
            <div className="desktop-placebet-submit-row">
              <button
                type="button"
                className="desktop-placebet-cancel-order"
                onClick={closePlaceBetManually}
              >
                Cancel Order
              </button>

              <button
                type="button"
                className="desktop-placebet-place-order"
                disabled={props?.placing || !Number(props?.StakeValue)}
                onClick={() => {
                  if (!props?.placing) {
                    props?.betPlace?.();
                  }
                }}
              >
                {props?.placing ? "Placing..." : "Place Order"}
              </button>
            </div>
          </div>
        </section>

        {stakeButtonEditorModal}
      </>
    );
  }

  return (
    <>
      <section
        className={[
          "mobile-placebet",
          displayIsLay ? "mobile-placebet-lay" : "mobile-placebet-back",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label="Place Bet"
      >
        <div className="mobile-placebet-content">
          {/* Selected runner and profit */}
          <div className="mobile-placebet-summary">
            <div className="mobile-placebet-market-copy">
              <span
                className="mobile-placebet-market-name"
                title={mobileMarketName}
              >
                {mobileMarketName}
              </span>

              {mobileEventName && (
                <strong
                  className="mobile-placebet-event-name"
                  title={mobileEventName}
                >
                  {mobileEventName}
                </strong>
              )}
            </div>

            <div className="mobile-placebet-limits">
              <span>MIN BET: {mobileMinBet}</span>
              <span>MAX BET: {mobileMaxBet}</span>
            </div>
          </div>

          {/* Light background behind Odds and Amount */}
          <div className="mobile-placebet-input-panel">
            {/* Odds and Amount labels */}
            <div className="mobile-placebet-field-labels">
              <span>Odds</span>
              <span>Stake</span>
            </div>

            {/* Odds spinner and amount input */}
            <div className="mobile-placebet-fields">
              <div className="mobile-placebet-odds-control">
                <button
                  type="button"
                  className="mobile-placebet-odds-button mobile-placebet-odds-minus"
                  // onClick={decreaseDesktopOdds}
                  disabled
                  aria-label="Decrease odds"
                >
                  <i className="fa fa-minus" aria-hidden="true" />
                </button>

                <input
                  type="text"
                  className="mobile-placebet-odds-input"
                  value={editableOdds}
                  inputMode="decimal"
                  // onChange={(event) => {
                  //   const value = event.target.value;

                  //   if (value === "") {
                  //     setEditableOdds("");
                  //     return;
                  //   }

                  //   if (/^\d*\.?\d*$/.test(value)) {
                  //     setEditableOdds(value);
                  //   }
                  // }}
                  // onBlur={() => {
                  //   updateEditableOdds(
                  //     editableOdds || 1.01
                  //   );
                  // }}
                  readOnly
                  aria-label="Odds"
                />

                <button
                  type="button"
                  className="mobile-placebet-odds-button mobile-placebet-odds-plus"
                  // onClick={increaseDesktopOdds}
                  disabled
                  aria-label="Increase odds"
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                </button>
              </div>

              <input
                type="number"
                min="0"
                step="1"
                className="mobile-placebet-amount-input"
                value={props?.StakeValue ?? 0}
                onChange={(event) => {
                  const rawValue = event.target.value;

                  if (rawValue === "") {
                    props?.placeStakeValue?.(0);
                    return;
                  }

                  const value = Number(rawValue);

                  if (Number.isFinite(value) && value >= 0) {
                    props?.placeStakeValue?.(value);
                  }
                }}
                onWheel={(event) => {
                  event.currentTarget.blur();
                }}
                // placeholder="Enter amount"
                aria-label="Bet amount"
              />
            </div>
          </div>

          {/* Dynamic admin stake buttons */}
          <div className="mobile-placebet-chips">
            {desktopStakeChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className="mobile-placebet-chip"
                onClick={() => addDesktopStake(chip.value)}
              >
                {String(chip.value).replace(/^\+/, "")}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="mobile-placebet-actions">
            <button
              type="button"
              className="mobile-placebet-min"
              onClick={setMinimumStake}
            >
              MIN
            </button>

            <button
              type="button"
              className="mobile-placebet-max"
              onClick={setMaximumStake}
            >
              MAX
            </button>

            <button
              type="button"
              className="mobile-placebet-edit"
              onClick={openStakeButtonEditor}
            >
              EDIT STAKE
            </button>

            <button
              type="button"
              className="mobile-placebet-clear"
              onClick={clearStakeAmount}
            >
              CLEAR
            </button>
          </div>

          <div className="mobile-placebet-submit-row">
            <button
              type="button"
              className="mobile-placebet-cancel"
              onClick={closePlaceBetManually}
            >
              CANCEL
            </button>

            <button
              type="button"
              className="mobile-placebet-submit"
              disabled={props?.placing || !Number(props?.StakeValue)}
              onClick={() => {
                if (!props?.placing) {
                  props?.betPlace?.();
                }
              }}
            >
              {props?.placing ? "PLACING..." : "PLACE BET"}
            </button>
          </div>

          {/* Team profit/loss */}
          <div className="mobile-placebet-exposure">
            {(() => {
              let runners =
                Array.isArray(props?.BetPlaceData?.runners) &&
                props.BetPlaceData.runners.length > 0
                  ? props.BetPlaceData.runners
                  : [
                      ...(props?.BetPlaceData?.team1
                        ? [
                            {
                              runner_name: props.BetPlaceData.team1,
                              exposure:
                                props?.BetPlaceData?.exposure_team1 ?? 0,
                            },
                          ]
                        : []),

                      ...(props?.BetPlaceData?.team2
                        ? [
                            {
                              runner_name: props.BetPlaceData.team2,
                              exposure:
                                props?.BetPlaceData?.exposure_team2 ?? 0,
                            },
                          ]
                        : []),

                      ...(props?.BetPlaceData?.runner_name &&
                      !props?.BetPlaceData?.team1 &&
                      !props?.BetPlaceData?.team2
                        ? [
                            {
                              runner_name: props.BetPlaceData.runner_name,
                              exposure: props?.BetPlaceData?.exposure ?? 0,
                            },
                          ]
                        : []),
                    ];

              if (runners.length === 0 && props?.BetPlaceData?.runner_name) {
                runners = [
                  {
                    runner_name: props.BetPlaceData.runner_name,
                    exposure: props?.BetPlaceData?.exposure ?? 0,
                  },
                ];
              }

              return runners.map((runner, index) => {
                const exposure = Number(runner?.exposure || 0);

                return (
                  <div
                    key={`${runner?.runner_name || "runner"}-${index}`}
                    className="mobile-placebet-exposure-row"
                  >
                    <span>{runner?.runner_name || "-"}</span>

                    <strong
                      className={
                        exposure > 0
                          ? "mobile-profit-positive"
                          : exposure < 0
                            ? "mobile-profit-negative"
                            : ""
                      }
                    >
                      {exposure.toFixed(2)}
                    </strong>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {stakeButtonEditorModal}
    </>
  );
};

export default PlaceBet;
